import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPriceSavingsMessage from "@MasterBluePrint/Overrides/Components/ProductPriceSavingsMessage";
import { getQuote, GetQuoteApiParameter } from "@MasterBluePrint/Services/CustomQuoteService";
import { VATForPDPSetting } from "@MasterBluePrint/Store/Context/ContextSelector";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";


interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    extendedStyles?: ProductPriceStyles;
    hideVat?: boolean;
    hideProductPriceSavingsMessage?: boolean;
    isPartOfCarousel?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    state,
    vatForPDP: VATForPDPSetting(state),
    currentPage: getCurrentPage(state),
    cart: getCurrentCartState(state).value,
    currentPageUrl: getLocation(state).pathname,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    callText?: TypographyPresentationProps;
    parentProductPLPInfoText?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            margin-bottom: 0.75rem;

            span {
                margin-right: .31250625rem !important;
            }
            span[data-test-selector="productPriceSavingsMessage"] del {
                font-size:.875rem !important;
            }

        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    priceWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${wrapInContainerStyles}
            p {
                margin-top: 1.3rem;
            }

            &.unitlabeluk {
                span[data-test-selector="productPrice_unitNetPrice"] {
                    font-size: 1.25rem;
                    color: ${themeColors.colorPrimary};
                }
                span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                    color: ${themeColors.colorCharcoalGrey};
                    text-transform: lowercase !important;
                    font-size: 1rem !important;
                    letter-spacing: 0.04rem;
                    line-height: 1.5rem;
                    font-family: Lato, sans-serif;
                }
                .vat-field {
                    color: ${themeColors.colorCharcoalGrey};
                    font-size: 0.75rem !important;
                    letter-spacing: 0.04rem;
                    line-height: 1.5rem;
                    font-family: Lato, sans-serif;
                    margin-top: 0px;
                    margin:0;
                }

                @media screen and (max-width: 1399px) {
                    span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                        margin-left: 0.31250625rem;
                    }                    
                }
            }
        `,
    },
    price: {
        priceText: {
            css: css`
                color: ${themeColors.colorpriceText} !important;
                font-family: 'Roboto', sans-serif;
                font-size: 1.625rem;
                font-weight: ${themeFontWeights.fontWeightForBold};
                letter-spacing: -.026rem;
                line-height: 1.4375rem;
                position: relative;

                @media screen and (max-width: 1399px) {
                    color: ${themeColors.colorpriceText} !important;
                    font-family: 'Roboto', sans-serif;
                    font-size: 1.125rem;
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    letter-spacing: -.022rem;
                }
            `,
        },
        callText: { weight: "bold" },
        parentProductPLPInfoText: {
            css: css`
                color: ${themeColors.colorDanger};
                font-family: Roboto, sans-serif;
                font-size: .875rem !important;
                font-weight: bold !important
            `,
        },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 1.5625rem;
                font-size: 1.68rem !important;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
                color: ${themeColors.colorMeasureText};
                text-transform: lowercase !important;
                font-size: 1rem !important;
                margin-right: .8rem;
                margin-left: .31250625rem;

                @media screen and (max-width: 1399px) {
                    color: ${themeColors.colorCharcoalGrey};
                    font-family: Lato, sans-serif;
                    font-size: .875rem;
                    letter-spacing: .003rem
                    line-height: 1.5rem;
                }
               
            `,
        },
        vatLabelText: {
            css: css`
                font-size: .75rem;
                margin-top: 0px;
                margin:0;
            `,
        },
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 0.625rem;
            display: flex;
            flex-wrap: wrap;
            ${wrapInContainerStyles}

            &.secondaryunitlabeluk {
                span[data-test-selector="productPrice_unitNetPrice"] {                                       
                    font-family: Roboto, sans-serif;
                    color: ${themeColors.colorRed};
                    font-size: 1.25rem;
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    letter-spacing: -0.0238rem;
                    line-height: 1.4375rem;
                }
                span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                    color: ${themeColors.colorCharcoalGrey};
                    text-transform: lowercase !important;
                    font-size: 1rem !important;
                    letter-spacing: 0.04rem;
                    line-height: 1.5rem;
                    font-family: Lato, sans-serif;
                    margin-left: 0.31250625rem;
                }
                .vat-field {
                    color: ${themeColors.colorCharcoalGrey};
                    font-size: .75rem !important;
                    letter-spacing: 0.04rem;
                    line-height: 1.5rem;
                    font-family: Lato, sans-serif;
                    margin-top:0px;
                }
                @media screen and (max-width: 1399px) {
                    span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                        margin-left: 0.31250625rem;
                    }                    
                }
            }
        `,
    },
    secondaryPrice: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 1.5625rem;
                
                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
            `,
        },
        vatLabelText: {
            css: css`
                font-size: .75rem;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: .5rem;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {

        size: "1rem",
        css: css`
        &:first-child {
        padding:.05rem .5rem;
        background: #002554;
        width: auto;
        color:#fff;
        margin: 0 0 1rem;
        max-width: max-content;
    }
    `,
    },
    quoteMessage: {
        text: { weight: "bold" },
    },

};

const SectionWrapper = getStyledWrapper("section");

const ProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    enableVat,
    vatPriceDisplay,
    extendedStyles,
    hideVat,
    hideProductPriceSavingsMessage,
    state,
    currentPage,
    vatForPDP,
    isPartOfCarousel = false,
    cart,
    currentPageUrl
}: Props) => {
    const currencyCode = state.context.session.currency?.currencyCode;
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));
    const [quote, setQuote] = useState<QuoteModel | null>(null);
    const [isQuoteCart, setIsQuoteCart] = useState(false);
    useEffect(() => {
        if (cart) {
            if (cart.properties?.isquote === "true") {
                setIsQuoteCart(true);
                const parameter: GetQuoteApiParameter = { quoteId: cart.properties?.quoteNumber };
                getQuoteData(parameter).then(quote => {
                    setQuote(quote);
                });
            }
        }
    }, [cart]);

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const currentPageType = currentPage.type;

    if (currentPageType === "ProductDetailsPage" && vatForPDP?.vatForPDP) {
        enableVat = true;
        hideVat = false;
        vatPriceDisplay = isPartOfCarousel === true ? "DisplayWithoutVat" : "DisplayWithAndWithoutVat";
    }

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                ?.description || pricingData.productInfo.unitOfMeasure
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    let pricing;
    if ((currentPageUrl.toLowerCase().includes("cart") || currentPageUrl.toLowerCase().includes("checkout")) && cart != null && cart.properties?.isquote === "true") {
        const quotePrice = quote?.cartLines?.find(line => line.productId === cartLineModel.productId);
        if (quotePrice) {
            pricing = quotePrice.pricing;
        }
    }
    else if ("product" in pricingData) {
        pricing = pricingData.productInfo.pricing;
    } else {
        pricing = pricingData.pricing;
    }


    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;
    const isParentProduct = "product" in pricingData ? pricingData.product.isVariantParent : false;

    const isRealTimePricingLoading =
        "product" in pricingData
            ? pricingData.productInfo.pricing === undefined && !pricingData.productInfo.failedToLoadPricing
            : false;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};

    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText}>{siteMessage("Pricing_SignInForPrice")}</Typography>
            </StyledWrapper>
        );
    }

    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const priceClassName = pricing && pricing.actualPriceDisplay && pricing.actualPriceDisplay.length > 10 ? 'long-length' : '';
    const ref = useRef<any>(null)

    return (
        <SectionWrapper {...styles.wrapper} ref={ref} className={`price-section ${priceClassName}`}>
            {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                />
            )}
            {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
            <StyledWrapper {...styles.priceWrapper} className={currencyCode === "GBP" ? 'unitlabeluk' : ''}>
                <Price
                    isParentProduct={isParentProduct}
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles}
                    hideVat={hideVat}
                    isQuoteCart={isQuoteCart}
                    isRealTimePricingLoading={isRealTimePricingLoading}
                />

                {!hideProductPriceSavingsMessage && pricing && (
                    <ProductPriceSavingsMessage
                        pricing={pricing}
                        qtyOrdered={qtyOrdered || 1}
                        showSavingsAmount={false}
                        showSavingsPercent={false}
                        currencySymbol={currencySymbol}
                        {...styles.savingsMessageText}
                    />
                )}

            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper} className={currencyCode === "GBP" ? 'secondaryunitlabeluk' : ''}>
                    <Price
                        isParentProduct={isParentProduct}
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                        hideVat={hideVat}
                        isQuoteCart={isQuoteCart}
                        isRealTimePricingLoading={isRealTimePricingLoading}
                    />
                </StyledWrapper>
            )}
            {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}

        </SectionWrapper>
    );
};
export async function getQuoteData(
    parameter: GetQuoteApiParameter,
): Promise<QuoteModel> {
    const quoteProduct = await getQuote(parameter);
    return quoteProduct;
}
interface PriceProps {
    isParentProduct: boolean;
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
    hideVat?: boolean;
    isQuoteCart?: boolean;
    isRealTimePricingLoading?: boolean;
}

const Price = ({
    isParentProduct,
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
    hideVat,
    isQuoteCart,
    isRealTimePricingLoading,
}: PriceProps) => {
    let salePriceLabelPrefix = "";
    if (pricing && salePriceLabel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
        if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }
    return (
        <>
            {!isParentProduct ? (<>
                {pricing ? (
                    (isQuoteCart ? (
                        (pricing.unitNetPrice || pricing.unitRegularPriceWithVat) > 0 ? (
                            <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                                {salePriceLabelPrefix}
                                {enableVat && displayWithVat
                                    ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).price !== 0 ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay : siteMessage("Price_Zero_Dollar_Label")
                                    : getUnitNetPrice(pricing, qtyOrdered || 1).price !== 0 ? getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay : siteMessage("Price_Zero_Dollar_Label")}
                            </Typography>
                        ) : (
                            <Typography {...styles.callText} data-test-selector="productPrice_unitNetPrice">
                                {siteMessage("Price_Zero_Dollar_Label")}
                            </Typography>
                        )
                    ) : (pricing.unitListPrice || pricing.unitListPriceWithVat) > 0 ? (
                        <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                            {salePriceLabelPrefix}
                            {enableVat && displayWithVat
                                ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).price !== 0 ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay : siteMessage("Price_Zero_Dollar_Label")
                                : getUnitNetPrice(pricing, qtyOrdered || 1).price !== 0 ? getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay : siteMessage("Price_Zero_Dollar_Label")}
                        </Typography>
                    ) : (
                        <Typography {...styles.callText} data-test-selector="productPrice_unitNetPrice">
                            {siteMessage("Price_Zero_Dollar_Label")}
                        </Typography>
                    ))

                ) : isRealTimePricingLoading ? (
                    <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice">
                        {currencySymbol}
                    </Typography>
                ) : (
                    <Typography {...styles.callText} data-test-selector="productPrice_unitNetPrice">
                        {siteMessage("Price_Zero_Dollar_Label")}
                    </Typography>
                )}
            </>) : (<Typography {...styles.parentProductPLPInfoText}>
                {siteMessage("ParentProduct_PLP_InfoText")}
            </Typography>)}

            {unitOfMeasure && showUnitOfMeasure && (
                <Typography {...styles.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
                    {unitOfMeasure}
                </Typography>
            )}
            {pricing && enableVat && !hideVat && (
                <Typography {...styles.vatLabelText} className="vat-field">
                    {displayWithVat ? `(${translate("Incl. VAT")})` : `(${translate("Excl. VAT")})`}
                </Typography>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPrice);
